import React, { useState, useEffect } from 'react';
import { ShoppingCart, Search, Plus, Minus, CreditCard, Banknote, User, Package, Clock, LogOut, MapPin, Settings, X, ChevronUp, ChevronDown, Edit, FileText, Star, Tag, Shirt, Scissors, Sparkles, Wind, Droplets, Flame, Gem, Ribbon, ShoppingBag, Layers, Waves, Umbrella, Sun, Zap, Award, Briefcase, Camera, Wrench, Maximize2, History, ExternalLink, type LucideIcon } from 'lucide-react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import PaymentModal from '../../components/PaymentModal';
import OrderCompleteModal from './OrderCompleteModal';
import QRScanner from '../../components/QRScanner';
import SyncButton from '../../components/SyncButton';

// Helper to get auth headers including tenant site ID
const getHeaders = () => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  };
  const activeSiteId = localStorage.getItem('activeSiteId');
  if (activeSiteId) {
    headers['x-site-id'] = activeSiteId;
  }
  return headers;
};

// Temporary inline API service until import path is fixed
const servicesAPI = {
  getServices: async () => {
    const response = await fetch('/api/pos/services', {
      headers: getHeaders(),
    });
    return response.json();
  },
};

const categoriesAPI = {
  getCategories: async () => {
    const response = await fetch('/api/pos/categories', {
      headers: getHeaders(),
    });
    return response.json();
  },
};

// Dynamic color palette for categories
const COLOR_PALETTE: Record<string, { bg: string; bgDark: string; border: string; borderActive: string; text: string; badge: string; serviceBg: string; serviceBorder: string; serviceHover: string; serviceText: string }> = {
  blue:   { bg: 'bg-blue-500',   bgDark: 'bg-blue-600',   border: 'border-blue-500',   borderActive: 'border-blue-700',   text: 'text-white', badge: 'bg-blue-200 text-blue-900',     serviceBg: 'bg-blue-100',   serviceBorder: 'border-blue-300',   serviceHover: 'hover:border-blue-500',   serviceText: 'text-blue-800' },
  purple: { bg: 'bg-purple-500', bgDark: 'bg-purple-600', border: 'border-purple-500', borderActive: 'border-purple-700', text: 'text-white', badge: 'bg-purple-200 text-purple-900', serviceBg: 'bg-purple-100', serviceBorder: 'border-purple-300', serviceHover: 'hover:border-purple-500', serviceText: 'text-purple-800' },
  green:  { bg: 'bg-green-500',  bgDark: 'bg-green-600',  border: 'border-green-500',  borderActive: 'border-green-700',  text: 'text-white', badge: 'bg-green-200 text-green-900',   serviceBg: 'bg-green-100',  serviceBorder: 'border-green-300',  serviceHover: 'hover:border-green-500',  serviceText: 'text-green-800' },
  orange: { bg: 'bg-orange-500', bgDark: 'bg-orange-600', border: 'border-orange-500', borderActive: 'border-orange-700', text: 'text-white', badge: 'bg-orange-200 text-orange-900', serviceBg: 'bg-orange-100', serviceBorder: 'border-orange-300', serviceHover: 'hover:border-orange-500', serviceText: 'text-orange-800' },
  pink:   { bg: 'bg-pink-500',   bgDark: 'bg-pink-600',   border: 'border-pink-500',   borderActive: 'border-pink-700',   text: 'text-white', badge: 'bg-pink-200 text-pink-900',     serviceBg: 'bg-pink-100',   serviceBorder: 'border-pink-300',   serviceHover: 'hover:border-pink-500',   serviceText: 'text-pink-800' },
  red:    { bg: 'bg-red-500',    bgDark: 'bg-red-600',    border: 'border-red-500',    borderActive: 'border-red-700',    text: 'text-white', badge: 'bg-red-200 text-red-900',       serviceBg: 'bg-red-100',    serviceBorder: 'border-red-300',    serviceHover: 'hover:border-red-500',    serviceText: 'text-red-800' },
  yellow: { bg: 'bg-yellow-500', bgDark: 'bg-yellow-600', border: 'border-yellow-500', borderActive: 'border-yellow-700', text: 'text-white', badge: 'bg-yellow-200 text-yellow-900', serviceBg: 'bg-yellow-100', serviceBorder: 'border-yellow-300', serviceHover: 'hover:border-yellow-500', serviceText: 'text-yellow-800' },
  teal:   { bg: 'bg-teal-500',   bgDark: 'bg-teal-600',   border: 'border-teal-500',   borderActive: 'border-teal-700',   text: 'text-white', badge: 'bg-teal-200 text-teal-900',     serviceBg: 'bg-teal-100',   serviceBorder: 'border-teal-300',   serviceHover: 'hover:border-teal-500',   serviceText: 'text-teal-800' },
  indigo: { bg: 'bg-indigo-500', bgDark: 'bg-indigo-600', border: 'border-indigo-500', borderActive: 'border-indigo-700', text: 'text-white', badge: 'bg-indigo-200 text-indigo-900', serviceBg: 'bg-indigo-100', serviceBorder: 'border-indigo-300', serviceHover: 'hover:border-indigo-500', serviceText: 'text-indigo-800' },
  cyan:   { bg: 'bg-cyan-500',   bgDark: 'bg-cyan-600',   border: 'border-cyan-500',   borderActive: 'border-cyan-700',   text: 'text-white', badge: 'bg-cyan-200 text-cyan-900',     serviceBg: 'bg-cyan-100',   serviceBorder: 'border-cyan-300',   serviceHover: 'hover:border-cyan-500',   serviceText: 'text-cyan-800' },
  gray:   { bg: 'bg-gray-500',   bgDark: 'bg-gray-600',   border: 'border-gray-500',   borderActive: 'border-gray-700',   text: 'text-white', badge: 'bg-gray-200 text-gray-900',     serviceBg: 'bg-gray-100',   serviceBorder: 'border-gray-300',   serviceHover: 'hover:border-gray-500',   serviceText: 'text-gray-800' },
};

interface Service {
  _id: string;
  name: string;
  category: string;
  basePrice: number;
  pricingType: 'per_item' | 'per_kg' | 'fixed';
  isExpress: boolean;
  expressSurcharge: number;
  subServices?: { _id: string; name: string; price?: number; isActive?: boolean }[];
}

const ICON_MAP: Record<string, LucideIcon> = {
  Shirt, Scissors, Sparkles, Wind, Droplets, Flame, Gem, Ribbon,
  ShoppingBag, Layers, Waves, Star, Umbrella, Sun, Zap, Award,
  Tag, Package, Briefcase,
};

const getServiceIcon = (iconName?: string): LucideIcon => {
  if (iconName && ICON_MAP[iconName]) return ICON_MAP[iconName];
  return Shirt;
};

interface OrderItem {
  cartKey: string;
  serviceId: string;
  service: Service;
  isCustomItem?: boolean;
  customName?: string;
  quantity: number;
  weight?: number;
  specialInstructions?: string;
  isExpress: boolean;
  totalPrice: number;
  unitPrice: number;
  subService?: { id: string; name: string };
}

interface Customer {
  _id: string;
  firstName: string;
  lastName: string;
  nickname?: string;
  email: string;
  phone?: string;
  address?: string;
  postcode?: string;
  city?: string;
  gdprConsent?: boolean;
  marketingConsent?: boolean;
  preferredNotificationMethod?: 'email' | 'sms' | 'both';
}

interface CustomerOrder {
  _id: string;
  orderNumber: string;
  status: string;
  paymentStatus: string;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;
  dueDate?: string;
  createdAt: string;
  itemCount?: number;
}

interface CategoryDef {
  id: string;
  name: string;
  color: string;
  icon?: string;
}

const POS: React.FC = () => {
  const { user, logout, activeSiteName } = useAuth();
  const [services, setServices] = useState<Service[]>([]);
  const [siteCategories, setSiteCategories] = useState<CategoryDef[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [serviceSearch, setServiceSearch] = useState('');
  const [cart, setCart] = useState<OrderItem[]>([]);
  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [ordersCustomer, setOrdersCustomer] = useState<Customer | null>(null);
  const [customerOrders, setCustomerOrders] = useState<CustomerOrder[]>([]);
  const [customerOrdersLoading, setCustomerOrdersLoading] = useState(false);
  const [customerOrdersError, setCustomerOrdersError] = useState('');
  const [showCartDrawer, setShowCartDrawer] = useState(false);
  const [showExpandedCart, setShowExpandedCart] = useState(false);
  const [customerFormErrors, setCustomerFormErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    postcode?: string;
    city?: string;
    address?: string;
    form?: string;
  }>({});
  const [newCustomer, setNewCustomer] = useState({
    firstName: '',
    lastName: '',
    nickname: '',
    email: '',
    phone: '',
    postcode: '',
    city: '',
    address: '',
    gdprConsent: false,
    marketingConsent: false,
    preferredNotificationMethod: 'email',
  });
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);
  const [completedOrderId, setCompletedOrderId] = useState<string | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [dueDate, setDueDate] = useState<string>(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });

  // Loyalty & Discount state
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [loyaltyRedeemableValue, setLoyaltyRedeemableValue] = useState(0);
  const [loyaltyPointValue, setLoyaltyPointValue] = useState(0.01);
  const [loyaltyEnabled, setLoyaltyEnabled] = useState(false);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<{ _id: string; code: string; name: string; type: string; value: number; discountAmount: number } | null>(null);
  const [discountValidating, setDiscountValidating] = useState(false);
  const [showDiscountSection, setShowDiscountSection] = useState(false);
  const [showCustomItemForm, setShowCustomItemForm] = useState(false);
  const [customItemName, setCustomItemName] = useState('');
  const [customItemPrice, setCustomItemPrice] = useState('');
  const [selectingService, setSelectingService] = useState<Service | null>(null);

  const { data: categoriesData } = useQuery('pos-categories', async () => {
    const activeSiteId = localStorage.getItem('activeSiteId');
    if (!activeSiteId) return [];
    const data = await categoriesAPI.getCategories();
    return Array.isArray(data) ? data : [];
  }, {
    enabled: !!localStorage.getItem('activeSiteId'),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (categoriesData) setSiteCategories(categoriesData);
  }, [categoriesData]);

  const { data: servicesData, error: servicesError } = useQuery('services', async () => {
    const activeSiteId = localStorage.getItem('activeSiteId');
    if (!activeSiteId) {
      console.log('No active site, skipping services fetch');
      return [];
    }
    // Add delay to prevent rapid calls
    await new Promise(resolve => setTimeout(resolve, 300));
    const data = await servicesAPI.getServices();
    return Array.isArray(data) ? data : data.services || [];
  }, {
    enabled: !!localStorage.getItem('activeSiteId'),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  const { data: customersData, error: customersError } = useQuery('customers', async () => {
    const activeSiteId = localStorage.getItem('activeSiteId');
    if (!activeSiteId) {
      console.log('No active site, skipping customers fetch');
      return [];
    }
    // Add delay to prevent rapid calls
    await new Promise(resolve => setTimeout(resolve, 300));
    const res = await fetch('/api/customers', {
      headers: getHeaders(),
    });
    const data = await res.json();
    return Array.isArray(data) ? data : data.customers || [];
  }, {
    enabled: !!localStorage.getItem('activeSiteId'),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  useEffect(() => {
    if (servicesData) setServices(servicesData);
  }, [servicesData]);

  useEffect(() => {
    if (customersData) setCustomers(customersData);
  }, [customersData]);

  useEffect(() => {
    if (servicesError) toast.error('Error loading services');
  }, [servicesError]);

  useEffect(() => {
    if (customersError) toast.error('Error loading customers');
  }, [customersError]);

  // Close drawer when a payment modal opens
  useEffect(() => {
    if (showPaymentModal) {
      setShowCartDrawer(false);
      setShowExpandedCart(false);
    }
  }, [showPaymentModal]);

  // Screen size setting: 'default' | '10inch' | '15inch'
  const [screenSize, setScreenSize] = useState<string>('default');
  const [vatRate, setVatRate] = useState(0.2);
  const [vatInclusive, setVatInclusive] = useState(false);
  const [showVatInfo, setShowVatInfo] = useState(true);
  const [showScanner, setShowScanner] = useState(false);

  // Fetch site settings (screen size) and loyalty settings once
  useEffect(() => {
    const activeSiteId = localStorage.getItem('activeSiteId');
    if (!activeSiteId) return;

    const fetchSiteSettings = async () => {
      try {
        const res = await fetch(`/api/sites/${activeSiteId}`, { headers: getHeaders() });
        if (res.ok) {
          const data = await res.json();
          if (data.settings?.screenSize) setScreenSize(data.settings.screenSize);
          setVatRate(typeof data.vatRate === 'number' ? data.vatRate : 0);
          setVatInclusive(data.vatInclusive === true);
          setShowVatInfo(data.showVatInfo !== false);
        }
      } catch (e) { /* ignore */ }
    };
    fetchSiteSettings();

    const fetchLoyaltySettings = async () => {
      try {
        const res = await fetch('/api/loyalty/settings', { headers: getHeaders() });
        if (res.ok) {
          const data = await res.json();
          setLoyaltyEnabled(data.enabled);
          setLoyaltyPointValue(data.pointValue || 0.01);
        }
      } catch (e) { /* ignore */ }
    };
    fetchLoyaltySettings();
  }, []);

  // Fetch loyalty balance when customer changes
  useEffect(() => {
    setRedeemPoints(0);
    if (!selectedCustomer || !loyaltyEnabled) {
      setLoyaltyPoints(0);
      setLoyaltyRedeemableValue(0);
      return;
    }
    const fetchLoyalty = async () => {
      try {
        const res = await fetch(`/api/loyalty/customer/${selectedCustomer._id}`, { headers: getHeaders() });
        if (res.ok) {
          const data = await res.json();
          setLoyaltyPoints(data.points || 0);
          setLoyaltyRedeemableValue(data.redeemableValue || 0);
        }
      } catch (e) { /* ignore */ }
    };
    fetchLoyalty();
  }, [selectedCustomer, loyaltyEnabled]);

  // Reset discount when cart empties
  useEffect(() => {
    if (cart.length === 0) {
      setAppliedDiscount(null);
      setDiscountCode('');
      setShowDiscountSection(false);
      setRedeemPoints(0);
    }
  }, [cart.length]);

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    setDiscountValidating(true);
    try {
      const res = await fetch('/api/discounts/validate', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          code: discountCode,
          subtotal: cart.reduce((sum, item) => sum + item.totalPrice, 0),
          customerId: selectedCustomer?._id
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || 'Invalid discount code');
        return;
      }
      setAppliedDiscount(data.discount);
      setShowDiscountSection(false);
      toast.success(`Discount "${data.discount.name}" applied!`);
    } catch (e) {
      toast.error('Failed to validate discount');
    } finally {
      setDiscountValidating(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
  };

  // Build categories list from site config
  const categories = [
    { id: 'all', name: 'All', color: 'gray' },
    ...siteCategories,
  ];

  // Helper to get color config for a category id
  const getCategoryColor = (catId: string) => {
    const cat = categories.find(c => c.id === catId);
    return COLOR_PALETTE[cat?.color || 'gray'] || COLOR_PALETTE.gray;
  };

  const filteredServices = services.filter((service) => {
    const matchesCategory = selectedCategory === 'all' || service.category === selectedCategory;
    const matchesSearch = service.name.toLowerCase().includes(serviceSearch.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const addServiceToCart = (service: Service, subService?: { _id: string; name: string; price?: number }) => {
    const cartKey = subService ? `${service._id}:${subService._id}` : service._id;
    const unitPrice = subService?.price ?? service.basePrice;
    setCart((prev) => {
      const existing = prev.find((i) => i.cartKey === cartKey);
      if (!existing) {
        return [
          ...prev,
          {
            cartKey,
            serviceId: service._id,
            service,
            subService: subService ? { id: subService._id, name: subService.name } : undefined,
            unitPrice,
            quantity: 1,
            isExpress: false,
            totalPrice: unitPrice,
          },
        ];
      }
      return prev.map((i) => {
        if (i.cartKey !== cartKey) return i;
        const quantity = i.quantity + 1;
        const base = service.pricingType === 'per_kg' && i.weight ? i.unitPrice * i.weight : i.unitPrice * quantity;
        const totalPrice = base + (i.isExpress && service.isExpress ? service.expressSurcharge : 0);
        return { ...i, quantity, totalPrice };
      });
    });
  };

  const addToCart = (service: Service) => {
    const options = (service.subServices || []).filter(option => option.isActive !== false);
    if (options.length > 0) return setSelectingService(service);
    addServiceToCart(service);
  };

  const addCustomItem = () => {
    const name = customItemName.trim();
    const price = Number(customItemPrice);
    if (!name) return toast.error('Enter a description for the custom item');
    if (!Number.isFinite(price) || price <= 0) return toast.error('Enter an amount greater than zero');

    const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const roundedPrice = Math.round(price * 100) / 100;
    const service: Service = { _id: id, name, category: 'custom', basePrice: roundedPrice, pricingType: 'fixed', isExpress: false, expressSurcharge: 0 };
    setCart((prev) => [...prev, { cartKey: id, serviceId: id, service, isCustomItem: true, customName: name, quantity: 1, isExpress: false, unitPrice: roundedPrice, totalPrice: roundedPrice }]);
    setCustomItemName('');
    setCustomItemPrice('');
    setShowCustomItemForm(false);
    toast.success('Custom item added');
  };

  const openCustomerOrders = async (customer: Customer) => {
    setOrdersCustomer(customer);
    setCustomerOrders([]);
    setCustomerOrdersError('');
    setCustomerOrdersLoading(true);
    setShowCustomerDropdown(false);
    try {
      const response = await fetch(`/api/customers/${customer._id}/orders`, { headers: getHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load customer orders');
      setCustomerOrders(Array.isArray(data) ? data : []);
    } catch (error: any) {
      setCustomerOrdersError(error.message || 'Failed to load customer orders');
    } finally {
      setCustomerOrdersLoading(false);
    }
  };

  const removeFromCart = (cartKey: string) => {
    setCart((prev) => prev.filter((i) => i.cartKey !== cartKey));
  };

  const updateQuantity = (cartKey: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(cartKey);
      return;
    }
    setCart((prev) =>
      prev.map((i) => {
        if (i.cartKey !== cartKey) return i;
        const base = i.service.pricingType === 'per_kg' && i.weight ? i.unitPrice * i.weight : i.unitPrice * quantity;
        const totalPrice = base + (i.isExpress && i.service.isExpress ? i.service.expressSurcharge : 0);
        return { ...i, quantity, totalPrice };
      })
    );
  };

  const updateWeight = (cartKey: string, weight: number) => {
    setCart((prev) =>
      prev.map((i) => {
        if (i.cartKey !== cartKey) return i;
        const base = i.unitPrice * weight;
        const totalPrice = base + (i.isExpress && i.service.isExpress ? i.service.expressSurcharge : 0);
        return { ...i, weight, totalPrice };
      })
    );
  };

  const updateUnitPrice = (cartKey: string, unitPrice: number) => {
    if (!Number.isFinite(unitPrice) || unitPrice < 0) return;
    setCart(prev => prev.map(i => {
      if (i.cartKey !== cartKey) return i;
      const multiplier = i.service.pricingType === 'per_kg' ? (i.weight || 0) : i.quantity;
      const totalPrice = unitPrice * multiplier + (i.isExpress && i.service.isExpress ? i.service.expressSurcharge : 0);
      return { ...i, unitPrice, totalPrice };
    }));
  };

  const handlePriceDraftChange = (cartKey: string, value: string) => {
    setPriceDrafts(prev => ({ ...prev, [cartKey]: value }));
    if (value.trim() === '') return;

    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed >= 0) {
      updateUnitPrice(cartKey, parsed);
    }
  };

  const finishPriceEdit = (cartKey: string) => {
    setPriceDrafts(prev => {
      if (!(cartKey in prev)) return prev;
      const next = { ...prev };
      delete next[cartKey];
      return next;
    });
  };

  const toggleExpress = (cartKey: string) => {
    setCart((prev) =>
      prev.map((i) => {
        if (i.cartKey !== cartKey) return i;
        const isExpress = !i.isExpress;
        const base = i.service.pricingType === 'per_kg' && i.weight ? i.unitPrice * i.weight : i.unitPrice * i.quantity;
        const totalPrice = base + (isExpress && i.service.isExpress ? i.service.expressSurcharge : 0);
        return { ...i, isExpress, totalPrice };
      })
    );
  };

  const calculateTotals = () => {
    const displayedSubtotal = cart.reduce((sum, i) => sum + i.totalPrice, 0);
    const subtotal = vatInclusive && vatRate > 0
      ? Math.round((displayedSubtotal / (1 + vatRate)) * 100) / 100
      : displayedSubtotal;
    let discountAmt = 0;
    if (appliedDiscount) {
      if (appliedDiscount.type === 'percentage') {
        discountAmt = Math.round(displayedSubtotal * (appliedDiscount.value / 100) * 100) / 100;
      } else {
        discountAmt = appliedDiscount.value;
      }
      if (discountAmt > displayedSubtotal) discountAmt = displayedSubtotal;
    }
    const loyaltyDiscount = Math.round(redeemPoints * loyaltyPointValue * 100) / 100;
    const vat = vatInclusive
      ? Math.round((displayedSubtotal - subtotal) * 100) / 100
      : Math.round(subtotal * vatRate * 100) / 100;
    const total = Math.max(0, displayedSubtotal + (vatInclusive ? 0 : vat) - discountAmt - loyaltyDiscount);
    return { subtotal, discountAmt, loyaltyDiscount, vat, total };
  };

  const { subtotal, discountAmt, loyaltyDiscount, vat, total } = calculateTotals();

  const validateNewCustomer = () => {
    const errors: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      postcode?: string;
      city?: string;
      address?: string;
      form?: string;
    } = {};

    const firstName = newCustomer.firstName.trim();
    const lastName = newCustomer.lastName.trim();
    const email = newCustomer.email.trim();
    const phone = newCustomer.phone.trim();
    const postcode = newCustomer.postcode.trim();
    const city = newCustomer.city.trim();

    const normalizePhoneToE164 = (raw: string) => {
      const stripped = raw.replace(/[()\-\s]/g, '');
      if (!stripped) return '';
      if (stripped.startsWith('+')) return stripped;
      if (stripped.startsWith('00')) return `+${stripped.slice(2)}`;
      if (stripped.startsWith('0')) return `+44${stripped.slice(1)}`;
      return `+${stripped}`;
    };

    if (!firstName) errors.firstName = 'First name is required';
    if (email) {
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!isValidEmail) errors.email = 'Enter a valid email address';
    }

    if (phone) {
      const e164 = normalizePhoneToE164(phone);
      const isValidE164 = /^\+?[1-9]\d{1,14}$/.test(e164);
      if (!isValidE164) {
        errors.phone = 'Phone must be in international format (e.g. +447886699820)';
      }
    }

    setCustomerFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveCustomer = async () => {
    setCustomerFormErrors({});
    if (!validateNewCustomer()) return;

    const normalizePhoneToE164 = (raw: string) => {
      const stripped = raw.replace(/[()\-\s]/g, '');
      if (!stripped) return '';
      if (stripped.startsWith('+')) return stripped;
      if (stripped.startsWith('00')) return `+${stripped.slice(2)}`;
      if (stripped.startsWith('0')) return `+44${stripped.slice(1)}`;
      return `+${stripped}`;
    };

    const payload = {
      ...newCustomer,
      phone: newCustomer.phone.trim() ? normalizePhoneToE164(newCustomer.phone.trim()) : '',
    };

    try {
      const url = isEditingCustomer && selectedCustomer ? `/api/customers/${selectedCustomer._id}` : '/api/customers';
      const method = isEditingCustomer ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        const serverErrors: {
          firstName?: string;
          lastName?: string;
          email?: string;
          phone?: string;
          postcode?: string;
          city?: string;
          address?: string;
          form?: string;
        } = {};

        if (data?.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
          for (const [key, val] of Object.entries(data.errors)) {
            if (typeof val === 'string') (serverErrors as any)[key] = val;
            if (val && typeof val === 'object' && 'message' in (val as any)) {
              (serverErrors as any)[key] = (val as any).message;
            }
          }
        }

        if (Array.isArray(data?.errors)) {
          for (const err of data.errors) {
            if (!err || typeof err !== 'object') continue;
            const field = (err as any).param || (err as any).path || (err as any).field;
            const message = (err as any).msg || (err as any).message;
            if (field && typeof message === 'string') {
              (serverErrors as any)[field] = message;
            }
          }
        }

        if (Array.isArray(data?.details)) {
          for (const item of data.details) {
            if (item && typeof item === 'object') {
              const field = (item as any).field;
              const message = (item as any).message;
              if (field && typeof message === 'string') (serverErrors as any)[field] = message;
            }
          }
        }

        setCustomerFormErrors((prev) => ({ ...prev, ...serverErrors }));
        toast.error(serverErrors.form || 'Failed to create customer');
        return;
      }
      const savedCustomer: Customer = data.customer || data;

      if (isEditingCustomer) {
        setCustomers((prev) => prev.map((c) => c._id === savedCustomer._id ? savedCustomer : c));
        toast.success('Customer updated');
      } else {
        setCustomers((prev) => [savedCustomer, ...prev]);
        toast.success('Customer created');
      }

      setSelectedCustomer(savedCustomer);
      setShowCustomerModal(false);
      setIsEditingCustomer(false);
      setCustomerSearch('');
      setNewCustomer({ firstName: '', lastName: '', nickname: '', email: '', phone: '', postcode: '', city: '', address: '', gdprConsent: false, marketingConsent: false, preferredNotificationMethod: 'email' });
      setCustomerFormErrors({});
    } catch (e) {
      console.error(e);
      setCustomerFormErrors({ form: 'Failed to save customer' });
      toast.error('Failed to save customer');
    }
  };

  const handleCheckoutClick = async () => {
    if (!selectedCustomer) {
      toast.error('Select a customer');
      return;
    }
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    for (const item of cart) {
      if (item.service.pricingType === 'per_kg' && (!item.weight || item.weight <= 0)) {
        toast.error(`${item.service.name} requires a valid weight`);
        return;
      }
    }

    setIsLoading(true);
    try {
      const payload: any = {
        customerId: selectedCustomer._id,
        siteId: user?.siteId,
        orderType: 'in_store',
        dueDate,
        items: cart.map((i) => ({
          serviceId: i.isCustomItem ? undefined : i.serviceId,
          isCustomItem: Boolean(i.isCustomItem),
          customName: i.isCustomItem ? i.customName : undefined,
          customPrice: i.isCustomItem ? i.totalPrice : undefined,
          quantity: i.quantity,
          weight: i.weight,
          isExpress: i.isExpress,
          specialInstructions: i.specialInstructions,
          unitPrice: i.unitPrice,
          subService: i.subService,
        })),
      };
      if (appliedDiscount) {
        payload.discountCode = appliedDiscount.code;
        payload.discountAmount = discountAmt;
      }
      if (redeemPoints > 0) {
        payload.loyaltyPointsRedeemed = redeemPoints;
        payload.loyaltyDiscount = loyaltyDiscount;
      }

      const res = await fetch('/api/pos/orders', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || 'Failed to create order');
        return;
      }

      const orderId = data?.order?._id || data?.order?.id || data?._id || data?.id;
      if (!orderId) {
        toast.error('Order created but missing order id');
        return;
      }

      setPendingOrderId(orderId);
      setShowPaymentModal(true);
    } catch (e) {
      console.error(e);
      toast.error('Failed to create order');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentCancel = async () => {
    if (pendingOrderId) {
      try {
        await fetch(`/api/pos/orders/${pendingOrderId}`, {
          method: 'DELETE',
          headers: getHeaders(),
        });
      } catch (e) {
        console.error('Failed to abandon order', e);
      }
    }
    setShowPaymentModal(false);
    setPendingOrderId(null);
  };

  const handlePaymentComplete = async (method: string, paymentAmount: number) => {
    if (!pendingOrderId) return;

    setIsLoading(true);
    try {
      if (method === 'pay_on_collection') {
        setCompletedOrderId(pendingOrderId);
        setShowPaymentModal(false);
        setPendingOrderId(null);
        toast.success('Order placed (Pay on Collection)!');
        return;
      }

      const payRes = await fetch(`/api/pos/orders/${pendingOrderId}/payments`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ amount: paymentAmount, method }),
      });

      const payData = await payRes.json();
      if (!payRes.ok) {
        toast.error(payData.error || 'Payment recording failed');
        return;
      }

      // Apply discount on backend
      if (appliedDiscount && pendingOrderId) {
        try {
          await fetch('/api/discounts/apply', {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ discountId: appliedDiscount._id, orderId: pendingOrderId, discountAmount: discountAmt }),
          });
        } catch (e) { console.error('Apply discount error:', e); }
      }

      // Redeem loyalty points on backend
      if (redeemPoints > 0 && selectedCustomer && pendingOrderId) {
        try {
          await fetch('/api/loyalty/redeem', {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ customerId: selectedCustomer._id, orderId: pendingOrderId, points: redeemPoints }),
          });
        } catch (e) { console.error('Redeem points error:', e); }
      }

      // Earn loyalty points for this order
      if (loyaltyEnabled && selectedCustomer && pendingOrderId && total > 0) {
        try {
          await fetch('/api/loyalty/earn', {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ customerId: selectedCustomer._id, orderId: pendingOrderId, amount: total }),
          });
        } catch (e) { console.error('Earn points error:', e); }
      }

      setCompletedOrderId(pendingOrderId);
      setShowPaymentModal(false);
      setPendingOrderId(null);
      toast.success(paymentAmount < total ? 'Order placed with deposit!' : 'Order placed successfully!');
    } catch (e) {
      console.error(e);
      toast.error('Failed to finalize order');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOrderCompleteClose = () => {
    setCart([]);
    setSelectedCustomer(null);
    setCompletedOrderId(null);
    setAppliedDiscount(null);
    setDiscountCode('');
    setRedeemPoints(0);
  };

  const visibleCustomers = customers
    .filter((c) => {
      const q = customerSearch.trim().toLowerCase();
      if (!q) return true;
      return (
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) ||
        (c.nickname || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q) ||
        (() => {
          const queryPhone = q.replace(/\D/g, '').replace(/^00/, '').replace(/^0/, '44');
          const customerPhone = (c.phone || '').replace(/\D/g, '').replace(/^00/, '').replace(/^0/, '44');
          return queryPhone.length >= 3 && customerPhone.includes(queryPhone);
        })()
      );
    })
    .slice(0, 8);

  // Screen-size compact flags (used throughout layout)
  const compact = screenSize !== 'default';
  const extraCompact = screenSize === '15inch';

  // ─── Cart Panel (shared between side panel and drawer) ───────────────────────
  const renderCartContents = (inDrawer = false) => (
    <>
      <div className={`flex-1 overflow-y-auto ${compact ? 'p-2' : 'p-4'}`}>
        {cart.length === 0 ? (
          <div className="text-center text-gray-500 mt-8">
            <ShoppingCart className={`${compact ? 'w-8 h-8 mb-2' : 'w-12 h-12 mb-4'} mx-auto text-gray-300`} />
            <p className={compact ? 'text-sm' : 'text-base'}>Cart is empty</p>
            <p className="text-sm">Add services to begin</p>
          </div>
        ) : (
          <div className={compact ? 'space-y-2' : 'space-y-4'}>
            {cart.map((item) => (
              <div key={item.cartKey} className={`bg-gray-50 rounded-xl border border-gray-100 ${compact ? 'p-2' : 'p-4'}`}>
                <div className={`flex items-start justify-between ${compact ? 'mb-1' : 'mb-3'}`}>
                  <div className="flex-1 pr-2">
                    <h4 className={`font-semibold text-gray-900 leading-tight ${compact ? 'text-xs' : 'text-base'}`}>{item.service.name}{item.subService ? ` — ${item.subService.name}` : ''}</h4>
                    <label className={`flex items-center gap-1 text-gray-500 mt-1 ${compact ? 'text-xs' : 'text-sm'}`}>
                      <span>Price £</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={priceDrafts[item.cartKey] ?? String(item.unitPrice)}
                        onFocus={(e) => e.currentTarget.select()}
                        onChange={(e) => handlePriceDraftChange(item.cartKey, e.target.value)}
                        onBlur={() => finishPriceEdit(item.cartKey)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.currentTarget.blur();
                          if (e.key === 'Escape') {
                            finishPriceEdit(item.cartKey);
                            e.currentTarget.blur();
                          }
                        }}
                        className="w-20 px-1.5 py-0.5 border border-gray-300 rounded bg-white"
                      />
                    </label>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.cartKey)}
                    className={`flex items-center justify-center rounded-full text-red-400 hover:text-red-600 hover:bg-red-50 active:bg-red-100 flex-shrink-0 transition-colors ${compact ? 'w-6 h-6' : 'w-9 h-9'}`}
                    aria-label="Remove item"
                  >
                    <X className={compact ? 'w-3.5 h-3.5' : 'w-5 h-5'} />
                  </button>
                </div>

                <div className={`flex items-center justify-between ${compact ? 'mb-1' : 'mb-3'}`}>
                  {!item.isCustomItem ? <div className="flex items-center space-x-1">
                    <button
                      onClick={() => updateQuantity(item.cartKey, item.quantity - 1)}
                      className={`flex items-center justify-center rounded-full bg-white border-2 border-gray-200 hover:border-primary-400 hover:bg-primary-50 active:bg-primary-100 transition-colors ${compact ? 'w-7 h-7' : 'w-10 h-10'}`}
                      aria-label="Decrease quantity"
                    >
                      <Minus className={compact ? 'w-3 h-3 text-gray-700' : 'w-4 h-4 text-gray-700'} />
                    </button>
                    <span className={`w-8 text-center font-bold text-gray-900 ${compact ? 'text-sm' : 'text-lg'}`}>{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.cartKey, item.quantity + 1)}
                      className={`flex items-center justify-center rounded-full bg-white border-2 border-gray-200 hover:border-primary-400 hover:bg-primary-50 active:bg-primary-100 transition-colors ${compact ? 'w-7 h-7' : 'w-10 h-10'}`}
                      aria-label="Increase quantity"
                    >
                      <Plus className={compact ? 'w-3 h-3 text-gray-700' : 'w-4 h-4 text-gray-700'} />
                    </button>
                  </div> : <span className="text-xs font-medium text-gray-500">One-off item</span>}
                  <div className={`font-bold text-primary-600 ${compact ? 'text-sm' : 'text-lg'}`}>£{item.totalPrice.toFixed(2)}</div>
                </div>

                {item.service.pricingType === 'per_kg' && (
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Weight (kg)</label>
                    <input
                      type="number"
                      value={item.weight ?? ''}
                      onChange={(e) => updateWeight(item.cartKey, Number(e.target.value) || 0)}
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg text-base focus:border-primary-400 focus:outline-none"
                      step="0.1"
                      min="0"
                    />
                  </div>
                )}

                {item.service.isExpress && (
                  <label className="flex items-center space-x-3 cursor-pointer py-1">
                    <div
                      onClick={() => toggleExpress(item.cartKey)}
                      className={`w-12 h-6 rounded-full flex-shrink-0 transition-colors relative cursor-pointer ${item.isExpress ? 'bg-primary-600' : 'bg-gray-200'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${item.isExpress ? 'translate-x-6' : 'translate-x-0'}`} />
                    </div>
                    <span className="text-sm text-gray-700 font-medium">Express (+£{item.service.expressSurcharge.toFixed(2)})</span>
                  </label>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Totals + Checkout */}
      <div className={`bg-white border-t border-gray-200 space-y-1.5 ${compact ? 'p-2' : 'p-3'}`}>
        {showCustomItemForm ? (
          <div className="rounded-lg border border-primary-200 bg-primary-50 p-1.5 space-y-1.5">
            <div className="flex gap-1.5">
              <input type="text" value={customItemName} onChange={(e) => setCustomItemName(e.target.value)} placeholder="Custom item" maxLength={120} autoFocus className="min-w-0 flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:border-primary-500 focus:outline-none" />
              <div className="relative w-24">
                <span className="absolute left-3 top-2 text-gray-500">£</span>
                <input type="number" value={customItemPrice} onChange={(e) => setCustomItemPrice(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addCustomItem(); }} placeholder="0.00" min="0.01" step="0.01" className="w-full pl-7 pr-2 py-2 border border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none" />
              </div>
            </div>
            <div className="flex gap-1.5">
              <button type="button" onClick={addCustomItem} className="flex-1 py-1.5 rounded-md bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700">Add item</button>
              <button type="button" onClick={() => { setShowCustomItemForm(false); setCustomItemName(''); setCustomItemPrice(''); }} className="px-2 py-1.5 rounded-md bg-white text-gray-600 text-xs border border-gray-200">Cancel</button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={() => setShowCustomItemForm(true)} className="w-full py-1.5 rounded-lg border border-dashed border-primary-300 text-primary-700 text-xs font-semibold hover:bg-primary-50 flex items-center justify-center gap-1.5">
            <Plus className="w-4 h-4" /> Add custom price item
          </button>
        )}
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-700 whitespace-nowrap">Collection Date</label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="min-w-0 flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:border-primary-400 focus:outline-none"
          />
        </div>

        {/* Discount Code */}
        {cart.length > 0 && (
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowDiscountSection((open) => !open)}
              className="w-full flex items-center justify-between px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              aria-expanded={showDiscountSection}
            >
              <span className="flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-purple-600" />
                Discount{appliedDiscount ? `: ${appliedDiscount.code}` : ''}
              </span>
              {showDiscountSection ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showDiscountSection && (appliedDiscount ? (
              <div className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-800">{appliedDiscount.code}</span>
                  <span className="text-xs text-purple-600">(-£{appliedDiscount.discountAmount.toFixed(2)})</span>
                </div>
                <button onClick={removeDiscount} className="text-purple-400 hover:text-purple-600"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="flex gap-1.5 px-2 pb-2">
                <input
                  type="text"
                  placeholder="Discount code"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyDiscount()}
                  className={`flex-1 min-w-0 border-2 border-gray-200 rounded-lg text-sm focus:border-purple-400 focus:outline-none uppercase ${compact ? 'px-2 py-1.5' : 'px-3 py-2'}`}
                />
                <button
                  onClick={handleApplyDiscount}
                  disabled={discountValidating || !discountCode.trim()}
                  className={`flex-shrink-0 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 ${compact ? 'px-2 py-1.5' : 'px-3 py-2'}`}
                >
                  {discountValidating ? '...' : 'Apply'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Loyalty Points Redeem */}
        {loyaltyEnabled && selectedCustomer && loyaltyPoints > 0 && cart.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-yellow-800 flex items-center gap-1.5"><Star className="w-4 h-4 text-yellow-500" />{loyaltyPoints} pts (£{loyaltyRedeemableValue.toFixed(2)})</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min={0}
                max={Math.min(loyaltyPoints, Math.floor(subtotal / loyaltyPointValue))}
                value={redeemPoints}
                onChange={(e) => setRedeemPoints(parseInt(e.target.value) || 0)}
                className="flex-1 accent-yellow-500"
              />
              <span className="text-xs font-medium text-yellow-700 w-20 text-right">
                {redeemPoints > 0 ? `-£${(redeemPoints * loyaltyPointValue).toFixed(2)}` : 'None'}
              </span>
            </div>
            {redeemPoints > 0 && <p className="text-xs text-yellow-600">Redeeming {redeemPoints} points</p>}
          </div>
        )}

        <div className="space-y-1.5">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Subtotal</span>
            <span>£{subtotal.toFixed(2)}</span>
          </div>
          {discountAmt > 0 && (
            <div className="flex justify-between text-sm text-purple-600">
              <span>Discount</span>
              <span>-£{discountAmt.toFixed(2)}</span>
            </div>
          )}
          {loyaltyDiscount > 0 && (
            <div className="flex justify-between text-sm text-yellow-600">
              <span>Loyalty Redeem</span>
              <span>-£{loyaltyDiscount.toFixed(2)}</span>
            </div>
          )}
          {showVatInfo && <div className="flex justify-between text-sm text-gray-600">
            <span>{vatInclusive ? 'VAT included' : 'VAT'} ({(vatRate * 100).toFixed(vatRate * 100 % 1 === 0 ? 0 : 1)}%)</span>
            <span>£{vat.toFixed(2)}</span>
          </div>}
          <div className="flex justify-between text-xl font-bold pt-1 border-t border-gray-100">
            <span>Total</span>
            <span className="text-primary-600">£{total.toFixed(2)}</span>
          </div>
        </div>

        {inDrawer && (
          <button
            onClick={() => { setShowCartDrawer(false); setShowExpandedCart(false); }}
            className="w-full py-3 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Continue Adding Items
          </button>
        )}

        <button
          onClick={() => { setShowCartDrawer(false); setShowExpandedCart(false); handleCheckoutClick(); }}
          disabled={cart.length === 0 || !selectedCustomer || isLoading}
          className={`w-full font-bold rounded-xl bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md ${compact ? 'py-2.5 text-sm' : 'py-4 text-lg'}`}
        >
          {isLoading ? 'Processing...' : `Checkout  £${total.toFixed(2)}`}
        </button>
        {!selectedCustomer && cart.length > 0 && (
          <p className="text-center text-xs text-amber-600">Select a customer to checkout</p>
        )}
      </div>
    </>
  );

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">

      {/* ── HEADER ───────────────────────────────────────────────────────── */}
      <header className={`bg-white shadow-sm border-b border-gray-200 flex-shrink-0 ${compact ? 'px-2 py-1' : 'px-4 py-3'}`}>
        <div className={`flex items-center justify-between ${compact ? 'h-10' : 'h-16'}`}>
          {/* Left: title + site */}
          <div className="flex items-center gap-4 min-w-0">
            <img src="/hmpos_logo.jpeg" alt="Logo" className={`${compact ? 'h-7' : 'h-10'} w-auto rounded`} />
            {activeSiteName && (
              <div className={`flex items-center font-medium text-primary-600 bg-primary-50 rounded-full border border-primary-100 ${compact ? 'text-xs px-2 py-0.5' : 'text-sm px-3 py-1.5'}`}>
                <MapPin className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} mr-1`} />
                {activeSiteName}
              </div>
            )}
            <div className="hidden md:flex items-center gap-1.5 text-xs text-gray-400">
              <Clock className="w-3.5 h-3.5" />
              <span>{new Date().toLocaleDateString()}</span>
            </div>
          </div>

          {/* Right: customer search + user menu + admin link */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <SyncButton />
            <Link to="/jobs" className={`inline-flex items-center rounded-xl bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 font-semibold ${compact ? 'p-2' : 'px-3 py-2.5 text-sm'}`} title="Pending jobs">
              <Wrench className="w-4 h-4" />
              {!compact && <span className="ml-1.5 hidden lg:inline">Jobs</span>}
            </Link>
            {/* Customer Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              <input
                type="text"
                placeholder={selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : 'Name, nickname or phone...'}
                value={customerSearch}
                onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                onFocus={() => setShowCustomerDropdown(true)}
                className={`pl-9 pr-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none text-sm transition-all ${compact ? 'py-1.5 w-36 sm:w-44' : 'py-2.5 w-40 sm:w-52 md:w-64'}`}
              />
              {selectedCustomer && !customerSearch && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {loyaltyEnabled && loyaltyPoints > 0 && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold bg-yellow-100 text-yellow-700 rounded-full">
                      <Star className="w-3 h-3" />{loyaltyPoints}
                    </span>
                  )}
                  <button
                    onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); }}
                    className="text-gray-300 hover:text-gray-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {showCustomerDropdown && (
                <div className="absolute left-0 mt-1.5 w-80 sm:w-96 bg-white shadow-xl rounded-xl border border-gray-200 z-50 overflow-hidden">
                  <div className="max-h-72 overflow-auto divide-y divide-gray-50">
                    {visibleCustomers.length === 0 ? (
                      <div className="p-4 text-sm text-gray-500 text-center">No customers found</div>
                    ) : (
                      visibleCustomers.map((c) => (
                        <div key={c._id} className="flex items-center hover:bg-primary-50 transition-colors">
                        <button
                          onClick={() => { setSelectedCustomer(c); setShowCustomerDropdown(false); setCustomerSearch(''); }}
                          className="min-w-0 flex-1 text-left px-4 py-3.5 active:bg-primary-100"
                        >
                          <div className="text-sm font-semibold text-gray-900">{c.firstName} {c.lastName}{c.nickname ? ` (${c.nickname})` : ''}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{c.email}{c.phone ? ` • ${c.phone}` : ''}</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => openCustomerOrders(c)}
                          className="mr-3 px-2.5 py-2 text-xs font-semibold text-primary-700 bg-white border border-primary-200 rounded-lg hover:bg-primary-100 flex items-center gap-1"
                          title="View customer orders"
                        >
                          <History className="w-3.5 h-3.5" /> Orders
                        </button>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="p-3 bg-gray-50 border-t border-gray-100 flex gap-2">
                    <button
                      onClick={() => {
                        setIsEditingCustomer(false);
                        setNewCustomer({ firstName: '', lastName: '', nickname: '', email: '', phone: '', postcode: '', city: '', address: '', gdprConsent: false, marketingConsent: false, preferredNotificationMethod: 'email' });
                        setShowCustomerDropdown(false);
                        setShowCustomerModal(true);
                      }}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2.5 text-sm font-semibold text-primary-700 bg-primary-50 hover:bg-primary-100 active:bg-primary-200 rounded-lg transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-1.5" />
                      Add New Customer
                    </button>
                    <button
                      onClick={() => setShowCustomerDropdown(false)}
                      className="px-4 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>

            {selectedCustomer && (
              <button
                type="button"
                onClick={() => openCustomerOrders(selectedCustomer)}
                className={`flex items-center gap-1.5 bg-primary-50 text-primary-700 rounded-xl hover:bg-primary-100 border border-primary-200 ${compact ? 'p-2' : 'px-3 py-2.5'}`}
                title="View selected customer's orders"
              >
                <History className="w-4 h-4" />
                {!compact && <span className="text-sm font-semibold hidden xl:inline">Orders</span>}
              </button>
            )}

            {/* Scan Receipt Button */}
            <button
              onClick={() => setShowScanner(true)}
              className={`flex items-center gap-1.5 bg-amber-50 text-amber-700 rounded-xl hover:bg-amber-100 active:bg-amber-200 transition-colors border border-amber-200 ${compact ? 'px-2 py-1.5' : 'px-3 py-2.5'}`}
              title="Scan receipt QR code"
            >
              <Camera className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
              <span className={`font-medium hidden sm:inline ${compact ? 'text-xs' : 'text-sm'}`}>Scan</span>
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu((v) => !v)}
                className={`flex items-center gap-2 bg-gray-100 rounded-xl hover:bg-gray-200 active:bg-gray-300 transition-colors ${compact ? 'px-2 py-1.5' : 'px-3 py-2.5'}`}
              >
                <User className={compact ? 'w-3.5 h-3.5 text-gray-600' : 'w-4 h-4 text-gray-600'} />
                <span className={`font-medium text-gray-700 hidden sm:inline ${compact ? 'text-xs' : 'text-sm'}`}>
                  {user ? `${user.firstName}` : 'User'}
                </span>
              </button>
              {showUserMenu && (
                <div className="absolute right-0 mt-1.5 w-48 bg-white rounded-xl shadow-lg py-1 z-50 border border-gray-100">
                  <button
                    onClick={logout}
                    className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 w-full"
                  >
                    <LogOut className="w-4 h-4 mr-2 text-gray-500" />
                    Logout
                  </button>
                </div>
              )}
            </div>

            {(user?.role === 'admin' || user?.role === 'super_admin') && (
              <>
                <Link
                  to="/pos/end-of-day"
                  className={`hidden sm:flex items-center gap-1.5 bg-green-50 text-green-700 rounded-xl hover:bg-green-100 active:bg-green-200 transition-colors ${compact ? 'px-2 py-1.5' : 'px-3 py-2.5'}`}
                >
                  <FileText className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
                  <span className={`font-medium ${compact ? 'text-xs' : 'text-sm'}`}>End of Day</span>
                </Link>
                <Link
                  to="/admin"
                  className={`hidden sm:flex items-center gap-1.5 bg-primary-50 text-primary-700 rounded-xl hover:bg-primary-100 active:bg-primary-200 transition-colors ${compact ? 'px-2 py-1.5' : 'px-3 py-2.5'}`}
                >
                  <Settings className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
                  <span className={`font-medium ${compact ? 'text-xs' : 'text-sm'}`}>Admin</span>
                </Link>
              </>
            )}
          </div>
        </div>
      </header >

      {/* ── MAIN CONTENT ─────────────────────────────────────────────────── */}
      < div className="flex-1 flex overflow-hidden" >

        {/* ── LEFT: services panel ─────────────────────────────────────── */}
        < div className="flex-1 flex flex-col overflow-hidden" >

          {/* Category tabs */}
          < div className={`bg-white border-b border-gray-200 flex-shrink-0 ${compact ? 'px-2 py-1' : 'px-3 py-2'}`} >
            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
              {categories.map((category) => {
                const palette = COLOR_PALETTE[category.color] || COLOR_PALETTE.gray;
                const isActive = selectedCategory === category.id;
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex-shrink-0 rounded-xl font-semibold transition-all whitespace-nowrap border-2 ${compact ? 'px-2.5 py-1.5 text-xs' : 'px-4 py-2.5 text-sm'} ${isActive
                      ? `${palette.bgDark} ${palette.text} shadow-md ${palette.borderActive} ring-2 ring-offset-1 ring-${category.color}-300`
                      : `${palette.bg} ${palette.text} ${palette.border} hover:shadow-md opacity-80 hover:opacity-100`
                      }`}
                  >
                    {category.name}
                  </button>
                );
              })}
            </div>
          </div >

          {/* Service search */}
          < div className={`bg-white border-b border-gray-100 flex-shrink-0 ${compact ? 'px-2 py-1.5' : 'px-4 py-3'}`} >
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              <input
                type="text"
                placeholder="Search services..."
                value={serviceSearch}
                onChange={(e) => setServiceSearch(e.target.value)}
                className={`w-full pl-10 pr-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none text-sm ${compact ? 'py-1.5' : 'py-2.5'}`}
              />
            </div>
          </div >

          {/* Services grid */}
          < div className={`flex-1 overflow-y-auto ${compact ? 'p-2' : 'p-4'}`} >
            {
              filteredServices.length === 0 ? (
                <div className="text-center text-gray-400 mt-16">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p className="text-base">No services found</p>
                </div>
              ) : (
                <div className={`grid gap-1.5 ${extraCompact ? 'grid-cols-4 md:grid-cols-5 lg:grid-cols-6' : compact ? 'grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7' : 'grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2'}`}>
                  {filteredServices.map((service) => {
                    const palette = getCategoryColor(service.category);
                    const cat = categories.find(c => c.id === service.category);
                    const IconComponent = getServiceIcon(cat?.icon);
                    return (
                      <button
                        key={service._id}
                        onClick={() => addToCart(service)}
                        className={`${palette.serviceBg} ${palette.serviceBorder} ${palette.serviceHover} rounded-lg shadow-sm border hover:shadow-md active:opacity-80 transition-all text-left group ${compact ? 'p-1.5' : 'p-2.5'}`}
                      >
                        <div className={`flex items-start ${compact ? 'gap-1' : 'gap-2'}`}>
                          <div className={`flex-shrink-0 rounded-md ${palette.badge} ${compact ? 'p-1' : 'p-1.5'}`}>
                            <IconComponent className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className={`font-semibold text-gray-900 leading-tight truncate group-hover:text-gray-800 ${compact ? 'text-[10px]' : 'text-xs'}`}>{service.name}</h3>
                            <p className={`font-bold ${palette.serviceText} mt-0.5 ${compact ? 'text-xs' : 'text-sm'}`}>£{service.basePrice.toFixed(2)}</p>
                          </div>
                        </div>
                        {service.isExpress && (
                          <p className={`text-orange-500 font-medium mt-0.5 ${compact ? 'text-[8px]' : 'text-[10px]'}`}>Express +£{service.expressSurcharge.toFixed(2)}</p>
                        )}
                      </button>
                    );
                  })}
                </div>
              )
            }
          </div >
        </div >

        {/* ── RIGHT: Cart side panel (hidden on narrow tablets) ────────── */}
        < div className={`bg-white border-l border-gray-200 flex-col flex-shrink-0 ${compact ? 'flex w-64' : 'hidden lg:flex w-80 xl:w-96'}`} >
          <div className={`border-b border-gray-200 flex-shrink-0 ${compact ? 'px-2 py-2' : 'px-4 py-3.5'}`}>
            <div className="flex items-center justify-between">
              <h2 className={`font-bold text-gray-900 flex items-center gap-2 ${compact ? 'text-sm' : 'text-lg'}`}>
                <ShoppingCart className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-primary-600`} />
                Cart
                {cart.length > 0 && (
                  <span className="bg-primary-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">{cart.length}</span>
                )}
              </h2>
              <button
                type="button"
                onClick={() => setShowExpandedCart(true)}
                className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                title="Expand basket"
                aria-label="Expand basket"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
              {selectedCustomer && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-sm text-primary-700 bg-primary-50 px-2.5 py-1 rounded-lg">
                    <User className="w-3.5 h-3.5" />
                    <span className="font-medium">{selectedCustomer.firstName}</span>
                  </div>
                  <button
                    onClick={() => {
                      setNewCustomer({
                        firstName: selectedCustomer.firstName || '',
                        lastName: selectedCustomer.lastName || '',
                        nickname: selectedCustomer.nickname || '',
                        email: selectedCustomer.email || '',
                        phone: selectedCustomer.phone || '',
                        postcode: selectedCustomer.postcode || '',
                        city: selectedCustomer.city || '',
                        address: selectedCustomer.address || '',
                        gdprConsent: selectedCustomer.gdprConsent || false,
                        marketingConsent: selectedCustomer.marketingConsent || false,
                        preferredNotificationMethod: selectedCustomer.preferredNotificationMethod || 'email',
                      });
                      setIsEditingCustomer(true);
                      setShowCustomerModal(true);
                    }}
                    className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Edit Customer"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
          {renderCartContents()}
        </div >
      </div >

      {/* ── BOTTOM CART FAB (tablets < lg) ──────────────────────────────── */}
      < div className={`fixed bottom-0 inset-x-0 z-40 ${compact ? 'hidden' : 'lg:hidden'}`} >
        {/* Backdrop */}
        {
          showCartDrawer && (
            <div
              className="fixed inset-0 bg-black/40 z-40"
              onClick={() => setShowCartDrawer(false)}
            />
          )
        }

        {/* Slide-up cart drawer */}
        <div className={`absolute bottom-0 inset-x-0 bg-white rounded-t-2xl shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-out ${showCartDrawer ? 'translate-y-0' : 'translate-y-full'}`}
          style={{ maxHeight: '85vh' }}>
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 flex-shrink-0">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-primary-600" />
              Cart ({cart.length})
            </h2>
            <div className="flex items-center gap-3">
              {selectedCustomer && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-sm text-primary-700 bg-primary-50 px-2.5 py-1 rounded-lg">
                    <User className="w-3.5 h-3.5" />
                    <span className="font-medium">{selectedCustomer.firstName}</span>
                  </div>
                  <button
                    onClick={() => {
                      setNewCustomer({
                        firstName: selectedCustomer.firstName || '',
                        lastName: selectedCustomer.lastName || '',
                        nickname: selectedCustomer.nickname || '',
                        email: selectedCustomer.email || '',
                        phone: selectedCustomer.phone || '',
                        postcode: selectedCustomer.postcode || '',
                        city: selectedCustomer.city || '',
                        address: selectedCustomer.address || '',
                        gdprConsent: selectedCustomer.gdprConsent || false,
                        marketingConsent: selectedCustomer.marketingConsent || false,
                        preferredNotificationMethod: selectedCustomer.preferredNotificationMethod || 'email',
                      });
                      setIsEditingCustomer(true);
                      setShowCartDrawer(false);
                      setShowCustomerModal(true);
                    }}
                    className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Edit Customer"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              )}
              <button
                onClick={() => setShowCartDrawer(false)}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto flex flex-col">
            {renderCartContents(true)}
          </div>
        </div>

        {/* Sticky bottom bar (always visible) */}
        {
          !showCartDrawer && (
            <button
              onClick={() => setShowCartDrawer(true)}
              className="w-full bg-primary-600 text-white flex items-center justify-between px-5 py-4 hover:bg-primary-700 active:bg-primary-800 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="relative">
                  <ShoppingCart className="w-6 h-6" />
                  {cart.length > 0 && (
                    <span className="absolute -top-2 -right-2 bg-white text-primary-600 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow">
                      {cart.length}
                    </span>
                  )}
                </div>
                <span className="font-semibold text-base">
                  {cart.length === 0 ? 'Cart is empty' : `${cart.length} item${cart.length !== 1 ? 's' : ''}`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold">£{total.toFixed(2)}</span>
                <ChevronUp className="w-5 h-5 opacity-80" />
              </div>
            </button>
          )
        }
      </div >

      {/* ── MODALS ───────────────────────────────────────────────────────── */}
      {showExpandedCart && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowExpandedCart(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-primary-600" />
                Basket ({cart.length})
              </h2>
              <button type="button" onClick={() => setShowExpandedCart(false)} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200" aria-label="Close expanded basket">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="flex-1 min-h-0 flex flex-col">
              {renderCartContents(true)}
            </div>
          </div>
        </div>
      )}
      {selectingService && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div><h3 className="text-lg font-bold text-gray-900">{selectingService.name}</h3><p className="text-sm text-gray-500">Choose a service option</p></div>
              <button onClick={() => setSelectingService(null)} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
              {(selectingService.subServices || []).filter(option => option.isActive !== false).map(option => (
                <button key={option._id} type="button" onClick={() => { addServiceToCart(selectingService, option); setSelectingService(null); }} className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-gray-100 hover:border-primary-300 hover:bg-primary-50 text-left">
                  <span className="font-semibold text-gray-900">{option.name}</span>
                  <span className="font-bold text-primary-700">£{(option.price ?? selectingService.basePrice).toFixed(2)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {
        showPaymentModal && pendingOrderId && (
          <PaymentModal
            orderId={pendingOrderId}
            amount={total}
            onClose={handlePaymentCancel}
            onComplete={handlePaymentComplete}
          />
        )
      }

      {
        completedOrderId && (
          <OrderCompleteModal
            orderId={completedOrderId}
            onClose={handleOrderCompleteClose}
          />
        )
      }

      {/* Add / Edit Customer Modal */}
      {
        showCustomerModal && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white w-full sm:rounded-2xl sm:max-w-lg overflow-hidden flex flex-col max-h-screen sm:max-h-[90vh]">
              {/* Modal header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
                <h3 className="text-lg font-bold text-gray-900">{isEditingCustomer ? 'Edit Customer' : 'Add New Customer'}</h3>
                <button
                  onClick={() => { setShowCustomerModal(false); setNewCustomer({ firstName: '', lastName: '', nickname: '', email: '', phone: '', postcode: '', city: '', address: '', gdprConsent: false, marketingConsent: false, preferredNotificationMethod: 'email' }); setCustomerFormErrors({}); }}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Modal body */}
              <div className="overflow-y-auto flex-1 p-5">
                {customerFormErrors.form && (
                  <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {customerFormErrors.form}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  {/* First name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First name <span className="text-red-500">*</span></label>
                    <input
                      className={`w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 ${customerFormErrors.firstName ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      value={newCustomer.firstName}
                      onChange={(e) => { setNewCustomer((p) => ({ ...p, firstName: e.target.value })); if (customerFormErrors.firstName) setCustomerFormErrors((p) => ({ ...p, firstName: undefined })); }}
                    />
                    {customerFormErrors.firstName && <div className="mt-1 text-xs text-red-600">{customerFormErrors.firstName}</div>}
                  </div>
                  {/* Last name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
                    <input
                      className={`w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 ${customerFormErrors.lastName ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      value={newCustomer.lastName}
                      onChange={(e) => { setNewCustomer((p) => ({ ...p, lastName: e.target.value })); if (customerFormErrors.lastName) setCustomerFormErrors((p) => ({ ...p, lastName: undefined })); }}
                    />
                    {customerFormErrors.lastName && <div className="mt-1 text-xs text-red-600">{customerFormErrors.lastName}</div>}
                  </div>
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nickname <span className="text-xs font-normal text-gray-400">(internal only)</span></label>
                    <input
                      className="w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 border-gray-200"
                      value={newCustomer.nickname}
                      maxLength={80}
                      onChange={(e) => setNewCustomer((p) => ({ ...p, nickname: e.target.value }))}
                    />
                  </div>
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      className={`w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 ${customerFormErrors.email ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      value={newCustomer.email}
                      onChange={(e) => { setNewCustomer((p) => ({ ...p, email: e.target.value })); if (customerFormErrors.email) setCustomerFormErrors((p) => ({ ...p, email: undefined })); }}
                    />
                    {customerFormErrors.email && <div className="mt-1 text-xs text-red-600">{customerFormErrors.email}</div>}
                  </div>
                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      className={`w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 ${customerFormErrors.phone ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      value={newCustomer.phone}
                      onChange={(e) => { setNewCustomer((p) => ({ ...p, phone: e.target.value })); if (customerFormErrors.phone) setCustomerFormErrors((p) => ({ ...p, phone: undefined })); }}
                    />
                    {customerFormErrors.phone && <div className="mt-1 text-xs text-red-600">{customerFormErrors.phone}</div>}
                  </div>
                  {/* Postcode */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                    <input
                      className={`w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 ${customerFormErrors.postcode ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      value={newCustomer.postcode}
                      onChange={(e) => { setNewCustomer((p) => ({ ...p, postcode: e.target.value })); if (customerFormErrors.postcode) setCustomerFormErrors((p) => ({ ...p, postcode: undefined })); }}
                    />
                    {customerFormErrors.postcode && <div className="mt-1 text-xs text-red-600">{customerFormErrors.postcode}</div>}
                  </div>
                  {/* City */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      className={`w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 ${customerFormErrors.city ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      value={newCustomer.city}
                      onChange={(e) => { setNewCustomer((p) => ({ ...p, city: e.target.value })); if (customerFormErrors.city) setCustomerFormErrors((p) => ({ ...p, city: undefined })); }}
                    />
                    {customerFormErrors.city && <div className="mt-1 text-xs text-red-600">{customerFormErrors.city}</div>}
                  </div>
                  {/* Address */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <textarea
                      className={`w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 resize-none ${customerFormErrors.address ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      rows={2}
                      value={newCustomer.address}
                      onChange={(e) => { setNewCustomer((p) => ({ ...p, address: e.target.value })); if (customerFormErrors.address) setCustomerFormErrors((p) => ({ ...p, address: undefined })); }}
                    />
                    {customerFormErrors.address && <div className="mt-1 text-xs text-red-600">{customerFormErrors.address}</div>}
                  </div>

                  {/* Preferences & Consents */}
                  <div className="col-span-2 grid grid-cols-1 gap-4 pt-4 border-t border-gray-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preferences & Consents</label>
                      <div className="space-y-3">
                        <label className="flex items-center text-sm font-medium text-gray-700">
                          <input
                            type="checkbox"
                            className="mr-2 rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-offset-2 focus:ring-primary-500"
                            checked={newCustomer.gdprConsent}
                            onChange={(e) => setNewCustomer((p) => ({ ...p, gdprConsent: e.target.checked }))}
                          />
                          GDPR Consent
                        </label>
                        <label className="flex items-center text-sm font-medium text-gray-700">
                          <input
                            type="checkbox"
                            className="mr-2 rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-offset-2 focus:ring-primary-500"
                            checked={newCustomer.marketingConsent}
                            onChange={(e) => setNewCustomer((p) => ({ ...p, marketingConsent: e.target.checked }))}
                          />
                          Marketing Consent
                        </label>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Notification Method</label>
                          <select
                            className="w-full border-2 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary-400 border-gray-200"
                            value={newCustomer.preferredNotificationMethod}
                            onChange={(e) => setNewCustomer((p) => ({ ...p, preferredNotificationMethod: e.target.value as any }))}
                          >
                            <option value="email">Email</option>
                            <option value="sms">SMS</option>
                            <option value="both">Both</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal footer */}
              <div className="flex gap-3 px-5 py-4 border-t border-gray-100 flex-shrink-0">
                <button
                  className="flex-1 py-3.5 text-base font-semibold rounded-xl border-2 border-gray-200 text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                  onClick={() => { setShowCustomerModal(false); setNewCustomer({ firstName: '', lastName: '', nickname: '', email: '', phone: '', postcode: '', city: '', address: '', gdprConsent: false, marketingConsent: false, preferredNotificationMethod: 'email' }); setCustomerFormErrors({}); }}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 py-3.5 text-base font-bold rounded-xl bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 transition-colors shadow-md"
                  onClick={handleSaveCustomer}
                >
                  {isEditingCustomer ? 'Save Changes' : 'Create Customer'}
                </button>
              </div>
            </div>
          </div>
        )
      }
      {/* Customer order history — staff only */}
      {ordersCustomer && (
        <div className="fixed inset-0 z-[70] bg-black/55 flex items-center justify-center p-3 sm:p-6" onClick={() => setOrdersCustomer(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><History className="w-5 h-5 text-primary-600" /> Customer Orders</h2>
                <p className="text-sm text-gray-500 truncate mt-0.5">
                  {ordersCustomer.firstName} {ordersCustomer.lastName}{ordersCustomer.nickname ? ` (${ordersCustomer.nickname})` : ''}
                  {ordersCustomer.phone ? ` • ${ordersCustomer.phone}` : ''}
                </p>
              </div>
              <button type="button" onClick={() => setOrdersCustomer(null)} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200" aria-label="Close customer orders">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-4 sm:p-5 bg-gray-50">
              {customerOrdersLoading ? (
                <div className="py-16 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-3" />
                  Loading orders...
                </div>
              ) : customerOrdersError ? (
                <div className="py-12 text-center">
                  <p className="text-red-600 mb-3">{customerOrdersError}</p>
                  <button type="button" onClick={() => openCustomerOrders(ordersCustomer)} className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold">Try again</button>
                </div>
              ) : customerOrders.length === 0 ? (
                <div className="py-16 text-center text-gray-500">
                  <Package className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                  No orders found for this customer.
                </div>
              ) : (
                <div className="space-y-2">
                  {customerOrders.map((order) => {
                    const balance = Math.max(0, Number(order.balanceAmount ?? (order.totalAmount - order.paidAmount)) || 0);
                    const collectionDate = order.dueDate ? new Date(order.dueDate).toLocaleDateString('en-GB') : 'Not set';
                    return (
                      <Link
                        key={order._id}
                        to={`/orders/${order._id}`}
                        onClick={() => setOrdersCustomer(null)}
                        className="block bg-white border border-gray-200 rounded-xl px-4 py-3 hover:border-primary-300 hover:shadow-sm transition-all"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-primary-700">{order.orderNumber}</span>
                              <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${order.status === 'ready' ? 'bg-purple-100 text-purple-800' : order.status === 'completed' ? 'bg-green-100 text-green-800' : order.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-800'}`}>
                                {order.status?.replace(/_/g, ' ') || 'received'}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${order.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' : order.paymentStatus === 'partial' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}`}>
                                {order.paymentStatus || 'pending'}
                              </span>
                            </div>
                            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                              <span>Created: {new Date(order.createdAt).toLocaleDateString('en-GB')}</span>
                              <span className="font-semibold text-gray-700">Collection: {collectionDate}</span>
                              {order.itemCount !== undefined && <span>{order.itemCount} item{order.itemCount === 1 ? '' : 's'}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <div className="text-right">
                              <div className="font-bold text-gray-900">£{Number(order.totalAmount || 0).toFixed(2)}</div>
                              {balance > 0 && <div className="text-xs font-semibold text-red-600">Balance £{balance.toFixed(2)}</div>}
                            </div>
                            <ExternalLink className="w-4 h-4 text-gray-400" />
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 bg-white text-xs text-gray-500 flex items-center justify-between flex-shrink-0">
              <span>{customerOrders.length} order{customerOrders.length === 1 ? '' : 's'}</span>
              <span>Select an order to view, collect or take payment</span>
            </div>
          </div>
        </div>
      )}
      {/* QR Scanner Modal */}
      {showScanner && (
        <QRScanner onClose={() => setShowScanner(false)} />
      )}
    </div >
  );
};

export default POS;
